/**
 * AllOrders Page
 *
 * Description: This page displays all orders in the admin dashboard. It includes a breadcrumb for navigation and an OrdersList component that renders a table with order details.
 *
 * Components Used:
 * - BreadCrumb: Displays the current navigation path, allowing easy navigation back to the dashboard.
 * - OrdersList: Renders a list of orders in a table format.
 *
 * File Name: AllOrders.tsx
 * Date: 15-08-2024
 * change date: 11/01/2024 updated file
 */

import React, { useState, useEffect } from "react";
import BreadCrumb from "../../Components/GlobalComponents/BreadCrumb";
import { IOrderItems, IqueryParams } from "src/types/GlobalInterfaces";
import { IPaginated } from "src/types/paginated";
import httpRequest from "src/helpers/httpRequest";
import qs from "qs";
import { toast } from "react-toastify";
import SearchFilterInput from "src/Components/GlobalComponents/SearchFilterInput";
import TableWrapper from "src/Components/GlobalComponents/TableWrapper";
import { CHECKBOX_INPUT, CURRENCY } from "src/lib/constans";
import {
  capitalize,
  convertToKebabCase,
  displayStatus,
  getLastSegment,
} from "src/helpers/helperFunctions";
import moment from "moment";
import { Link } from "react-router-dom";
import eyeIcon from "src/assets/images/view.png";
import deleteIcon from "src/assets/images/delete-icon.png";
import ConfirmationModal from "src/Components/GlobalComponents/ConfirmationModal";
import * as XLSX from "xlsx";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { canWrite } from "src/helpers/helperFunctions";

interface IOrderResponse {
  documents: IOrderItems[];
  paginated: IPaginated;
  pendingCount: number;
  cancelCount: number;
  completedCount: number;
  deliveredCount: number;
  deletedCount: number;
}
type statusEnums =
  | "pending"
  | "delivered"
  | "deleted"
  | "cancelled"
  | "completed";

const AllOrders: React.FC = () => {
  const { userDetails } = useSelector((store: RootState) => store.appReducer);
  const hasWriteAccess = canWrite(userDetails, "diagnosticOrder", false);

  // List of tab names for filtering orders
  const tabList: { label: string; key: "" | statusEnums }[] = [
    { label: "All Orders", key: "" },
    { label: "Pending", key: "pending" },
    { label: "Completed", key: "completed" },
    { label: "Cancelled", key: "cancelled" },
    { label: "Delivered", key: "delivered" },
    { label: "Deleted", key: "deleted" },
  ];
  const [activeTab, setActiveTab] = useState<"" | statusEnums>("");

  // States for loading status, orders data, counts, and pagination
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [allOrders, setAllOrders] = useState<IOrderItems[]>([]); // Holds all orders data

  const [count, setCount] = useState<{
    totalCount: number;
    pendingCount: number;
    cancelCount: number;
    completedCount: number;
    deliveredCount: number;
    deletedCount: number;
  }>({
    totalCount: 0,
    pendingCount: 0,
    cancelCount: 0,
    completedCount: 0,
    deliveredCount: 0,
    deletedCount: 0,
  });
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  }); // Handles pagination
  const [queryParams, setQueryParams] = useState<IqueryParams>({
    page: 1,
    limit: 10,
    status: "",
    search: "",
    bool: false,
  });
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [deleteStates, setDeleteStates] = useState<{
    deleteLoader: boolean;
    deleteModal: boolean;
    deleteId: null | string;
  }>({
    deleteLoader: false,
    deleteModal: false,
    deleteId: null,
  });
  const handleDelete = (id: string) => {
    if (!hasWriteAccess) {
      return;
    }
    setDeleteStates((prev) => ({
      ...prev,
      deleteModal: true,
      deleteId: id,
    }));
  };
  const deleteModaClose = () => {
    setDeleteStates((prev) => ({
      ...prev,
      deleteModal: false,
      deleteId: null,
    }));
  };
  const confirmDelete = async () => {
    setDeleteStates((prev) => ({ ...prev, deleteLoader: true }));
    const { res, err } = await httpRequest<{ message: string }>({
      method: "delete",
      path: `/diagnostic-order/delete/${deleteStates?.deleteId}`,
      params: {},
    });
    if (res) {
      toast(res?.message || "Success", { type: "success" });
      // handleTabClick("deleted");
      setDeleteStates((prev) => ({
        ...prev,
        deleteModal: false,
        deleteId: null,
      }));
      setQueryParams((prev) => ({ ...prev, bool: !prev?.bool }));
    } else {
      toast(err, { type: "error" });
    }
    setDeleteStates((prev) => ({ ...prev, deleteLoader: false }));
  };
  const handleSelectAll = () => {
    setSelectedRows(allOrders.map((v) => v._id));
  };
  const onToogleSelect = (id: string) => {
    if (selectedRows.includes(id)) {
      // if is exist
      setSelectedRows((prev) => prev.filter((v) => v !== id));
    } else {
      setSelectedRows((prev) => [...prev, id]);
    }
  };
  // Handles tab click to filter orders
  const handleTabClick = (tabName: "" | statusEnums) => {
    setActiveTab(tabName);
    setQueryParams((prev) => ({
      ...prev,
      status: tabName,
      page: 1,
    }));
  };

  // Updates search text in query parameters
  const handleChangeText = (txt: string) => {
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      search: txt,
    }));
  };

  // Handles page change in pagination
  const handlePageChange = (e: number) => {
    setQueryParams((prev) => ({
      ...prev,
      page: e,
    }));
  };

  const downloadFile = () => {
    let data = allOrders;
    if (selectedRows.length > 0) {
      data = data.filter((mha) => selectedRows.includes(mha._id));
    }
    let seriesArrayObjectConvert = data.map((mn) => {
      return {
        "Order ID": `${String(mn.serialOrderId)}`,
        Date: moment(mn.createdAt).format("DD-MM-YYYY"),
        "Customer Name": `${mn.firstName ? capitalize(mn.firstName) : ""} ${
          mn.lastName ? capitalize(mn.lastName) : ""
        }`,
        Email: mn.email,
        "Phone Number": String(mn.mobile),
        Amount: `${CURRENCY} ${mn?.discountedPrice || mn?.totalAmount || "-"}`,
        Status: displayStatus(mn.status),
      };
    });
    const ws = XLSX.utils.json_to_sheet(seriesArrayObjectConvert);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.writeFile(wb, "phamacy-order.csv");
  };

  // API_CALL: Fetches all orders from the server
  const fetchAllOrders = async () => {
    setIsLoader(true);
    const { res, err } = await httpRequest<IOrderResponse>({
      path: `/diagnostic-order/admin/all-orders?${qs.stringify(queryParams)}`,
    });
    if (res) {
      let {
        documents = [],
        paginated,
        pendingCount = 0,
        cancelCount = 0,
        completedCount = 0,
        deliveredCount = 0,
        deletedCount = 0,
      } = res;
      setAllOrders(documents);
      setPaginated(paginated);
      setCount({
        // totalCount: 
        // paginated.totalItems || 0,
        totalCount:
          pendingCount +
          cancelCount +
          completedCount +
          deliveredCount,
        pendingCount,
        cancelCount,
        completedCount,
        deliveredCount,
        deletedCount,
      });
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  useEffect(() => {
    fetchAllOrders();
  }, [queryParams]);
  return (
    <>
      <ConfirmationModal
        show={deleteStates.deleteModal}
        onClose={deleteModaClose}
        title="Are you sure you want to delete?"
        leftBtnText="No"
        rightBtnText="Yes"
        onConfirm={confirmDelete}
        loader={deleteStates.deleteLoader}
      />
      <BreadCrumb
        current="Test Orders"
        previous="dashboard"
        navigateUrl="/admin"
        AllOrders={true}
        onPressExport={downloadFile}
      />
      <div className="table-container">
        <div className="flex justify-between items-center">
          <div className="products-tabs-btn">
            {tabList.map((item) => (
              <button
                key={item.key}
                className={`font-size-15px font-Poppins-Medium ${
                  activeTab === item.key && "active"
                }`}
                onClick={() => handleTabClick(item.key)}
                id={`${convertToKebabCase(item.key || "all-orders")}-tab`}
              >
                {item.key === ""
                  ? `All Orders (${count.totalCount})`
                  : item.key === "pending"
                  ? `Pending (${count.pendingCount})`
                  : item.key === "cancelled"
                  ? `Canceled (${count.cancelCount})`
                  : item.key === "completed"
                  ? `Completed (${count.completedCount})`
                  : item.key === "delivered"
                  ? `Delivered (${count.deliveredCount})`
                  : item.key === "deleted"
                  ? `Deleted (${count.deletedCount})`
                  : ""}
              </button>
            ))}
          </div>
          {/* <div className='printing-and-export-btn'>
                        <button onClick={downloadFile} disabled={!allOrders?.length} className='font-size-15px text-white font-poppins-Medium'>
                            <img src={exportIcon} alt="" />
                            <span>Export</span>
                        </button>
                    </div> */}
        </div>
        <div className="flex margin-top-issue-resolve-dash justify-end">
          <SearchFilterInput
            key={activeTab}
            inputExtraStyle={{ marginBottom: 0 }}
            onChangeText={handleChangeText}
            searchId="search-input"
          />
        </div>
        <hr className="products-tabs-hr-allOrder" />
        <TableWrapper
          isLoading={isLoader}
          thead={[
            CHECKBOX_INPUT,
            "Order ID",
            "Date",
            "Customer Name",
            "Email",
            "Phone Number",
            "Amount",
            "Status",
            "Action",
          ]}
          page={paginated?.currentPage || 1}
          total={paginated?.totalPages || 0}
          columnWidth={"6% 10% 10% 14% 20% 14% 10% 9% 7%"}
          onChange={handlePageChange}
          selectedRows={selectedRows}
          totalLength={allOrders?.length || 1}
          clearAll={() => setSelectedRows([])}
          selectAll={handleSelectAll}
        >
          <div className="tbody-content-height tbody-content-height-allOrder">
            {allOrders?.map((item: IOrderItems, index: number) => (
              <ul
                key={index}
                className={`font-size-16px tbody-light-table font-Poppins-Regular grid ${
                  !item?.adminNotified ? `font-bold` : ``
                }`}
                style={{
                  gridTemplateColumns: "6% 10% 10% 14% 20% 14% 10% 9% 7%",
                }}
              >
                <li>
                  <input
                    type="checkbox"
                    className="w-4 h-4"
                    checked={selectedRows.includes(item._id)}
                    onChange={() => onToogleSelect(item._id)}
                    id={`checkbox-${index + 1}`}
                  />
                </li>
                <li><span className="truncate">{getLastSegment(item?.serialOrderId)}</span></li>
                <li><span className="truncate">{moment(item?.createdAt).format("DD-MM-YYYY")}</span></li>
                <li className="capitalize"><span className="truncate">{`${item?.name || ""}`}</span></li>
                <li><span className="truncate">{item?.email || "-"}</span></li>
                <li><span className="truncate">{item?.mobile || "-"}</span></li>
                <li><span className="truncate">{`${CURRENCY} ${
                  item?.discountedPrice || item?.totalAmount || "-"
                }`}</span></li>
                <li
                  style={{
                    paddingLeft: 0,
                    justifyContent: "center",
                  }}
                >
                  <span
                    style={{
                      backgroundColor: displayStatus(item?.status)?.bgColor,
                      color: displayStatus(item?.status)?.color,
                    }}
                    className="py-1 px-4 font-size-13px font-Poppins-SemiBold capitalize rounded"
                  >
                    {displayStatus(item?.status)?.text || ""}
                  </span>
                </li>
                <li className="action-last-colum">
                  <Link
                    to={
                      !hasWriteAccess ? "#" : `/order-details/${item?._id}/view?type=diagnostic`
                    }
                  >
                    <img
                      src={eyeIcon}
                      alt=""
                      className={`${!hasWriteAccess ? "read_access" : ""}`}
                      id={`view-btn-${index + 1}`}
                    />
                  </Link>
                  {item?.status !== "deleted" ? (
                    <button
                      onClick={() => handleDelete(item?._id)}
                      id={`delete-btn-${index + 1}`}
                    >
                      <img
                        src={deleteIcon}
                        alt=""
                        className={`${!hasWriteAccess ? "read_access" : ""}`}
                      />
                    </button>
                  ) : null}
                </li>
              </ul>
            ))}
          </div>
        </TableWrapper>
      </div>
    </>
  );
};

export default AllOrders;
